/**
 * Coquille applicative : identité, navigation, bascule de thème.
 *
 * Le logo est un tracé, pas une image : trois points et une ligne de contrôle qui
 * franchit une limite. C'est le geste que le produit rend visible, et un SVG en ligne
 * reste net à toutes les résolutions sans requête supplémentaire.
 */

import { LogOut, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";

import { ROLE_LABEL } from "./authApi";
import { cn } from "./ui/primitives";
import { useSession } from "./ui/session";
import { useTheme } from "./ui/theme";

export function Logo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 28 28"
      className={cn("h-6 w-6", className)}
      aria-hidden="true"
    >
      <rect x="1" y="1" width="26" height="26" rx="7" className="fill-accent" />
      <path
        d="M5 18.5 L10 13 L14.5 16 L19 8 L23 12.5"
        fill="none"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="stroke-accent-ink"
        opacity="0.95"
      />
      <line
        x1="4"
        y1="10"
        x2="24"
        y2="10"
        strokeWidth="1"
        strokeDasharray="2 2"
        className="stroke-accent-ink"
        opacity="0.45"
      />
      <circle cx="19" cy="8" r="2.4" className="fill-accent-ink" />
    </svg>
  );
}

export interface Tab {
  id: string;
  label: string;
  icon: ReactNode;
  hint: string;
}

export function AppShell({
  tabs,
  current,
  onNavigate,
  children,
}: {
  tabs: Tab[];
  current: string;
  onNavigate: (id: string) => void;
  children: ReactNode;
}) {
  const { mode, toggle } = useTheme();
  const { identity, signOut } = useSession();

  return (
    <div className="flex min-h-screen bg-bg text-ink">
      {/* --- Rail de navigation --- */}
      <aside className="sticky top-0 hidden h-screen w-56 shrink-0 flex-col border-r border-line bg-surface md:flex">
        <div className="flex items-center gap-2.5 px-4 py-4">
          <Logo />
          <div className="leading-tight">
            <div className="text-[0.95rem] font-semibold tracking-tight">
              Capalys
            </div>
            <div className="text-[0.68rem] text-faint">
              Maîtrise des procédés
            </div>
          </div>
        </div>

        <nav className="flex flex-col gap-0.5 px-2">
          {tabs.map((tab) => {
            const active = tab.id === current;
            return (
              <button
                key={tab.id}
                onClick={() => onNavigate(tab.id)}
                className={cn(
                  "group flex items-start gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors",
                  active
                    ? "bg-accent-soft text-accent"
                    : "text-muted hover:bg-surface-2 hover:text-ink",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 shrink-0",
                    active ? "text-accent" : "text-faint",
                  )}
                >
                  {tab.icon}
                </span>
                <span className="min-w-0">
                  <span className="block text-[0.83rem] font-medium">
                    {tab.label}
                  </span>
                  <span className="block text-[0.7rem] leading-tight text-faint">
                    {tab.hint}
                  </span>
                </span>
              </button>
            );
          })}
        </nav>

        <div className="mt-auto border-t border-line p-2">
          {identity && (
            // Le rôle est affiché en permanence, et pas seulement dans un menu : c'est
            // lui qui explique pourquoi un bouton manque. Un technicien qui ne voit pas
            // « Analyser un fichier » doit comprendre d'un regard que ce n'est pas une
            // panne mais son périmètre.
            <div className="mb-1 rounded-md px-2.5 py-2">
              <div
                className="truncate text-[0.78rem] font-medium"
                title={identity.email}
              >
                {identity.email}
              </div>
              <div className="text-[0.68rem] text-faint">
                {identity.role ? ROLE_LABEL[identity.role] : "Sans rôle"}
              </div>
              <button
                onClick={() => void signOut()}
                className="mt-1.5 flex items-center gap-1.5 text-[0.75rem] text-muted transition-colors hover:text-ink"
              >
                <LogOut className="h-3.5 w-3.5" />
                Se déconnecter
              </button>
            </div>
          )}
          <button
            onClick={toggle}
            className="flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-[0.78rem] text-muted transition-colors hover:bg-surface-2 hover:text-ink"
          >
            {mode === "dark" ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
            {mode === "dark" ? "Thème clair" : "Thème sombre"}
          </button>
          <div className="px-2.5 pt-1 pb-1 text-[0.65rem] text-faint">
            Démonstration · aucune donnée conservée
          </div>
        </div>
      </aside>

      {/* --- Contenu --- */}
      <div className="min-w-0 flex-1">
        {/* Barre mobile : le rail latéral disparaît sous 768 px. */}
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-line bg-surface/90 px-4 py-2 backdrop-blur md:hidden">
          <Logo className="h-5 w-5" />
          <span className="text-[0.9rem] font-semibold">Capalys</span>
          <div className="ml-auto flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => onNavigate(tab.id)}
                className={cn(
                  "rounded-md px-2 py-1 text-[0.75rem]",
                  tab.id === current
                    ? "bg-accent-soft text-accent"
                    : "text-muted",
                )}
              >
                {tab.label}
              </button>
            ))}
            <button
              onClick={toggle}
              className="rounded-md px-2 py-1 text-muted"
            >
              {mode === "dark" ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </button>
            {identity && (
              <button
                onClick={() => void signOut()}
                className="rounded-md px-2 py-1 text-muted"
                aria-label="Se déconnecter"
              >
                <LogOut className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        <main className="mx-auto max-w-6xl px-4 py-6 md:px-8">{children}</main>
      </div>
    </div>
  );
}
